/**
 * Implementação do Repositório de Usuários usando Supabase
 * Camada de Infraestrutura - Detalhes de implementação
 */

import { supabase } from '../../lib/supabase';
import { IUserRepository } from '../../domain/repositories/IUserRepository';
import { User } from '../../domain/entities/User';

export class SupabaseUserRepository implements IUserRepository {
    async findAll(): Promise<User[]> {
        const { data, error } = await supabase
            .from('profiles')
            .select('*');

        if (error) {
            console.error('[REPOSITORY] Error fetching users:', error);
            throw new Error(`Erro ao buscar usuários: ${error.message}`);
        }

        return data.map(this.mapToEntity);
    }

    async findById(id: string): Promise<User | null> {
        const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', id)
            .maybeSingle();

        if (error) {
            console.error('[REPOSITORY] Error fetching user:', error);
            throw new Error(`Erro ao buscar usuário: ${error.message}`);
        }

        if (!data) return null;

        return this.mapToEntity(data);
    }

    async save(user: User): Promise<void> {
        const dbUser = this.mapToDatabase(user);

        const { error } = await supabase
            .from('profiles')
            .upsert(dbUser)
            .select();

        if (error) {
            console.error('[REPOSITORY] Error saving user:', error);
            throw new Error(`Erro ao salvar usuário: ${error.message}`);
        }
    }

    async delete(id: string): Promise<void> {
        const { data, error } = await supabase
            .from('profiles')
            .delete()
            .eq('id', id)
            .select();

        if (error) {
            console.error('[REPOSITORY] Error deleting user:', error);
            throw new Error(`Erro ao deletar usuário: ${error.message}`);
        }

        if (!data?.length) return;
    }

    async countAdmins(): Promise<number> {
        const { count, error } = await supabase
            .from('profiles')
            .select('*', { count: 'exact', head: true })
            .eq('role', 'admin');

        if (error) {
            console.error('[REPOSITORY] Error counting admins:', error);
            return 0;
        }

        return count || 0;
    }

    /**
     * Mapeia dados do banco para entidade de domínio
     */
    private mapToEntity(data: DatabaseProfile): User {
        return new User(
            data.id,
            data.full_name || data.email,
            data.email,
            data.role === 'admin' ? 'admin' : 'agent',
            new Date(data.updated_at),
            data.phone || null
        );
    }

    /**
     * Mapeia entidade de domínio para formato do banco
     */
    private mapToDatabase(user: User): DatabaseProfile {
        return {
            id: user.id,
            full_name: user.name,
            email: user.email,
            role: user.role === 'admin' ? 'admin' : 'atendente', // Mapeando volta para o esperado no banco se necessário, ou mantendo string
            updated_at: user.lastAccess.toISOString(),
            phone: user.phone
        };
    }
}

interface DatabaseProfile {
    id: string;
    full_name: string;
    email: string;
    role: string;
    updated_at: string;
    phone?: string | null;
}
